import React, { useState, useCallback } from 'react'
import Cropper from 'react-easy-crop'
import type { Area } from 'react-easy-crop'
import { X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react'

interface AvatarCropModalProps {
  imageSrc: string
  onCropDone: (blob: Blob) => void
  onCancel: () => void
}

/**
 * Crop + zoom modal for avatar images. Outputs a 256x256 circular-cropped JPEG blob.
 */
export const AvatarCropModal: React.FC<AvatarCropModalProps> = ({ imageSrc, onCancel, onCropDone }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }, [])

  const handleSave = useCallback(async () => {
    if (!croppedAreaPixels) return

    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.src = imageSrc

    await new Promise<void>((resolve) => {
      image.onload = () => resolve()
      if (image.complete) resolve()
    })

    const canvas = document.createElement('canvas')
    const size = 256
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0, 0, size, size,
    )

    canvas.toBlob(
      (blob) => { if (blob) onCropDone(blob) },
      'image/jpeg',
      0.9,
    )
  }, [croppedAreaPixels, imageSrc, onCropDone])

  const handleReset = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <h3 className="text-sm font-medium text-white">Position Your Photo</h3>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-white/10 text-white/40 hover:text-white/70 transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Cropper area */}
        <div className="relative w-full" style={{ height: 300 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={1}
            cropShape="round"
            showGrid={false}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        {/* Controls */}
        <div className="px-4 py-3 space-y-3">
          {/* Zoom slider */}
          <div className="flex items-center gap-3">
            <ZoomOut className="h-4 w-4 text-white/40 flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-orange-500 h-1"
            />
            <ZoomIn className="h-4 w-4 text-white/40 flex-shrink-0" />
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleReset}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-white/50 hover:text-white/70 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition-colors"
            >
              <RotateCcw className="h-3 w-3" /> Reset
            </button>
            <div className="flex-1" />
            <button
              onClick={onCancel}
              className="px-4 py-2 rounded-xl text-xs text-white/50 hover:text-white/70 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 rounded-xl text-xs font-medium text-white bg-orange-500/20 hover:bg-orange-500/30 border border-orange-500/30 transition-colors"
            >
              Save Photo
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
